import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideStore } from '@ngxs/store';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { NgApexchartsModule } from 'ng-apexcharts';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { setOptions } from '@googlemaps/js-api-loader';
import { HeaderInterceptor } from '@core/interceptors/header.interceptor';
import { ApiInterceptor } from '@core/interceptors/api.interceptor';
import { provideInsights } from '@core/services/common/insights.service';
import { ErrorInterceptor, GlobalErrorHandler, isOfficeEnvironment, UnauthorizedInterceptor } from 'ngx-q360-lib';
import { appState } from '@app/store';
import { Configuration } from '@environments/configuration';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { routes } from '@app/app.routes';
import { AuthConfig, OAuthModuleConfig, OAuthStorage, provideOAuthClient } from 'angular-oauth2-oidc';
import { authAppInitializerFactory } from '@app/auth/auth-app-initializer.factory';
import { AuthService } from '@app/auth/auth.service';
import { authConfig, authConfigTeams } from '@app/auth/auth-config';
import { authModuleConfig } from '@app/auth/auth-module-config';
import { AuthGuardWithForcedLogin } from '@app/auth/auth-guard-with-forced-login.service';
import { AuthGuard } from '@app/auth/auth-guard.service';
import { CDK_DRAG_CONFIG } from '@angular/cdk/drag-drop';
import { environment } from '@environments/environment';
import { provideSvgSprites } from 'ngxtension/svg-sprite';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { NgxsActionsExecutingModule } from '@ngxs-labs/actions-executing';
import { provideTransloco } from '@jsverse/transloco';
import { TranslocoHttpLoader } from '../transloco-loader';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { provideMarkdown } from 'ngx-markdown';

const storageFactory = (): OAuthStorage => {
  return sessionStorage;
};

const authConfigFactory = (): AuthConfig => {
  return isOfficeEnvironment() ? authConfigTeams : authConfig;
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideOAuthClient(),
    AuthService,
    AuthGuard,
    AuthGuardWithForcedLogin,
    provideAppInitializer(() => {
      const initializerFn = authAppInitializerFactory(inject(AuthService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      setOptions({
        key: environment.googleMapsKey,
      });
    }),
    { provide: AuthConfig, useFactory: authConfigFactory },
    { provide: OAuthModuleConfig, useValue: authModuleConfig },
    { provide: OAuthStorage, useFactory: storageFactory },
    { provide: HTTP_INTERCEPTORS, useClass: HeaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UnauthorizedInterceptor,
      multi: true,
    },
    { provide: CDK_DRAG_CONFIG, useValue: { zIndex: 99999 } },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideRouter(routes, withComponentInputBinding(), withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideStore(appState, { developmentMode: !Configuration.isProduction }),
    importProvidersFrom(NgApexchartsModule, NgxsActionsExecutingModule.forRoot()),
    withNgxsReduxDevtoolsPlugin({ disabled: Configuration.isProduction }),
    provideInsights({ enabled: Configuration.isProduction }),
    provideSvgSprites({
      name: 'icons',
      baseUrl: '/assets/icons/icons-sprite.svg',
    }),
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: { disableTooltipInteractivity: true } },
    provideTransloco({
      config: {
        availableLangs: ['en', 'nb'],
        defaultLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoMessageformat(),
    provideMarkdown(),
  ],
};
